/**
 * @fileoverview Gardens on Grow: the public listing of gardens at /gardens, and
 * a plant search across them. No account needed.
 *
 * Top to bottom, as the owner's approved mock has it: the public top bar (GardenFrame), "Gardens on
 * Grow" and "Real gardens, shared by the people who grow them."; a search box
 * ("Search a plant, like cucumbers"); the sorts as pills (Recently updated,
 * Most plants, Near my ZIP); and the gardens as cards (cover, "Amy's garden",
 * "@amy", "22 plants · 4 beds", "Updated 2 days ago"), twenty at a
 * time with "Show more". Typing two or more letters swaps the list for
 * "Searching "cucumber"", "3 gardens growing cucumber" and the public plants
 * that match, each with its newest public photo, stage, variety and garden,
 * opening its own page. A garden card opens the garden's page.
 *
 * "Near my ZIP" lists the gardens nearest the five-digit US ZIP the visitor
 * types (kept in this browser, GARDENS_ZIP_STORAGE_KEY); the server orders them
 * by the distance between ZIP centres and sends no distance, ZIP or town of
 * any garden, so it is sorting only. Their location is never asked for or
 * used. A ZIP the server does not know lists Recently updated, with a short
 * note. Grow does not use zones, so none is shown anywhere. Only what a
 * garden's own public page already shows is listed; the backend sends no
 * town, address, notes or profile photo, and opening this page counts no visit.
 *
 * The page wears Grow's own cream and green (the `grow` theme class on its own
 * root) whatever theme the visitor's browser last kept, as the mock shows it:
 * a visitor who has never signed in has no theme class on the page at all.
 *
 * Where: the route /gardens (App.tsx), public, outside the Layout; linked from
 * the bottom of every garden page.
 * API: GET /gardens/directory and GET /gardens/directory/plants
 * (getGardenDirectory, searchDirectoryPlants).
 * Settings: GARDENS_SEARCH_DELAY_MS, GARDENS_SEARCH_MIN and GARDENS_ZIP_STORAGE_KEY
 * (config/tunables/garden).
 * Words (en/es): gardensPage.*, gardenPage.page.* (card lines).
 * Watch out: a read that failed on our side (D31, lib/serverFailure) says the
 * standard sentence, reported to Sentry, with the support address as a link.
 * A slow search never overwrites a newer one (each read checks it is still the
 * one asked for).
 *
 * @module GardensPage
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import {
  getGardenDirectory,
  searchDirectoryPlants,
  type DirectoryGarden,
  type DirectoryPlant,
  type DirectorySort,
} from '@/shared/api/gardens';
import Spinner from '@/shared/components/ui/Spinner';
import { Chip } from '@/shared/components/ui/Chip';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import {
  GARDENS_SEARCH_DELAY_MS, GARDENS_SEARCH_MIN, GARDENS_ZIP_STORAGE_KEY,
} from '@/config/tunables/garden';
import { GardenFrame } from './gardenPageParts';
import { GARDEN_COLUMN_CLASS, SECTION_HEADING_CLASS } from './gardenPageSections';
import { GardenRow, PlantResult } from './gardensPageParts';

/** The sorts, in the order the pills show them. */
const SORTS: DirectorySort[] = ['recent', 'plants', 'near'];

/**
 * The ZIP this browser kept from the last visit, if it is five digits.
 *
 * @returns The ZIP ("27601"), or '' (none kept, or storage is refused).
 */
function keptZip(): string {
  try {
    const zip = window.localStorage.getItem(GARDENS_ZIP_STORAGE_KEY);
    return zip && /^[0-9]{5}$/.test(zip) ? zip : '';
  } catch {
    return '';
  }
}

/** One list being read a page at a time: what it holds so far, and where it stands. */
interface Reading<T> {
  /** Everything read so far, in order. */
  items: T[];
  /** How many there are in all. */
  total: number;
  /** The last page read (0 before the first). */
  page: number;
  /** How many a page holds. */
  pageSize: number;
  /** How many gardens the plants are in (a plant search only). */
  gardens: number;
  /** A read is under way. */
  loading: boolean;
  /** What a failed read threw, or null. */
  error: unknown;
}

/** A list with nothing read yet. */
const EMPTY_READING = { items: [], total: 0, page: 0, pageSize: 0, gardens: 0, loading: true, error: null };

/**
 * Reads a list a page at a time: the first page again whenever `key` changes
 * (a slow read for an earlier key is dropped), and the next page on `more`.
 *
 * @param key - What is being asked for; null reads nothing and holds an empty list.
 * @param read - Reads one page of it (1-based) for the key in force.
 * @returns The list so far, and `more` to read the next page.
 */
function usePagedList<T>(
  key: string | null,
  read: (page: number) => Promise<{ items: T[]; total: number; pageSize: number; gardens?: number }>,
): { list: Reading<T>; more: () => void } {
  const [list, setList] = React.useState<Reading<T>>(EMPTY_READING as Reading<T>);
  /** The key whose answers are wanted; an answer for another is dropped. */
  const wanted = React.useRef<string | null>(key);
  const reader = React.useRef(read);
  reader.current = read;

  /**
   * Reads one page and adds it to the list.
   *
   * @param page - From 1; 1 starts the list afresh.
   */
  const load = React.useCallback((page: number) => {
    const asked = wanted.current;
    setList((was) => ({ ...(page === 1 ? EMPTY_READING as Reading<T> : was), loading: true, error: null }));
    reader.current(page)
      .then((got) => {
        if (wanted.current !== asked) return;
        setList((was) => ({
          items: page === 1 ? got.items : [...was.items, ...got.items],
          total: got.total,
          page,
          pageSize: got.pageSize,
          gardens: got.gardens ?? 0,
          loading: false,
          error: null,
        }));
      })
      .catch((error) => {
        if (wanted.current !== asked) return;
        setList((was) => ({ ...was, loading: false, error }));
      });
  }, []);

  React.useEffect(() => {
    wanted.current = key;
    if (key === null) {
      setList({ ...(EMPTY_READING as Reading<T>), loading: false });
      return;
    }
    load(1);
  }, [key, load]);

  /** Reads the next page. */
  const more = React.useCallback(() => load(list.page + 1), [load, list.page]);
  return { list, more };
}

/**
 * The "Show more" button under a list with more to read.
 *
 * @param props.list - The list.
 * @param props.more - Reads the next page.
 */
const ShowMore: React.FC<{ list: Reading<unknown>; more: () => void }> = ({ list, more }) => {
  const { reactTranslate: t } = useTranslation();
  if (list.loading) {
    return list.page > 0 ? <div className="flex justify-center py-4"><Spinner size={16} /></div> : null;
  }
  if (list.items.length >= list.total) return null;
  return (
    <div className="flex justify-center pt-2">
      <button type="button" onClick={more} className="min-h-[44px] rounded-full border bg-card px-6 text-sm font-medium hover:bg-muted">
        {t('gardensPage.more')}
      </button>
    </div>
  );
};

/**
 * Gardens on Grow, at /gardens: the gardens as cards with a plant search and
 * three sorts.
 */
export const GardensPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const [sort, setSort] = React.useState<DirectorySort>('recent');
  /** What is in the ZIP box (digits only). */
  const [zipText, setZipText] = React.useState(keptZip);
  /** The box lost focus once: only then is a short ZIP called wrong. */
  const [zipTouched, setZipTouched] = React.useState(false);
  /** The server did not know the ZIP asked for: the list is Recently updated. */
  const [zipUnknown, setZipUnknown] = React.useState(false);
  const [text, setText] = React.useState('');
  /** The search once the typing paused: what is asked. */
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    const timer = window.setTimeout(() => setQuery(text.trim()), GARDENS_SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [text]);

  const searching = query.length >= GARDENS_SEARCH_MIN;
  /** The ZIP in force: five digits typed. Near my ZIP without one lists as Recently updated, as the backend does. */
  const zip = /^[0-9]{5}$/.test(zipText) ? zipText : '';

  const gardens = usePagedList<DirectoryGarden>(
    searching ? null : `${sort}|${sort === 'near' ? zip : ''}`,
    async (page) => {
      const got = await getGardenDirectory({ sort, zip, page });
      setZipUnknown(got.zipKnown === false);
      return { items: got.gardens, total: got.total, pageSize: got.pageSize };
    },
  );
  const plants = usePagedList<DirectoryPlant>(
    searching ? query : null,
    async (page) => {
      const got = await searchDirectoryPlants({ q: query, page });
      return { items: got.plants, total: got.total, pageSize: got.pageSize, gardens: got.gardens };
    },
  );

  /**
   * Takes what is typed in the ZIP box (digits only, five at most) and keeps
   * a whole ZIP here and in this browser; anything shorter forgets the kept one.
   *
   * @param typed - The box's text.
   */
  const typeZip = (typed: string): void => {
    const next = typed.replace(/[^0-9]/g, '').slice(0, 5);
    setZipText(next);
    try {
      if (next.length === 5) window.localStorage.setItem(GARDENS_ZIP_STORAGE_KEY, next);
      else window.localStorage.removeItem(GARDENS_ZIP_STORAGE_KEY);
    } catch { /* private mode: it is kept for this visit only */ }
  };

  const list = searching ? plants.list : gardens.list;
  const failed = list.error;
  const empty = !list.loading && !failed && list.items.length === 0;
  const found = plants.list.gardens === 1 ? 'gardensPage.found_one' : 'gardensPage.found';

  return (
    <div className="grow">
      <GardenFrame bare>
        <div className={cn(GARDEN_COLUMN_CLASS, 'space-y-5 pb-12 pt-6')}>
          <header className="space-y-2">
            <h1 className="font-grow text-3xl font-bold leading-tight sm:text-4xl">{t('gardensPage.title')}</h1>
            <p className="text-base text-muted-foreground">{t('gardensPage.subtitle')}</p>
          </header>

          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input
              type="search"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={t('gardensPage.searchPlaceholder')}
              aria-label={t('gardensPage.searchLabel')}
              maxLength={60}
              enterKeyHint="search"
              className="h-12 w-full rounded-2xl border bg-card pl-12 pr-12 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
            />
            {text && (
              <button
                type="button"
                aria-label={t('gardensPage.clear')}
                onClick={() => { setText(''); setQuery(''); }}
                className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>

          {!searching && (
            <>
              <div role="group" aria-label={t('gardensPage.sortLabel')} className="flex flex-wrap gap-2">
                {SORTS.map((key) => (
                  <Chip key={key} pressed={sort === key} onClick={() => setSort(key)}>
                    {t(`gardensPage.sort.${key}`)}
                  </Chip>
                ))}
              </div>
              {sort === 'near' && (
                <div className="space-y-1">
                  <label className="flex items-center gap-3 text-sm font-medium">
                    {t('gardensPage.zipLabel')}
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={5}
                      value={zipText}
                      onChange={(event) => typeZip(event.target.value)}
                      onBlur={() => setZipTouched(true)}
                      placeholder={t('gardensPage.zipPlaceholder')}
                      className="h-11 w-32 rounded-xl border bg-card px-3 text-base"
                    />
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {zipTouched && zipText && !zip
                      ? t('gardensPage.zipBad')
                      : zip && zipUnknown ? t('gardensPage.zipUnknown') : t('gardensPage.zipHint')}
                  </p>
                </div>
              )}
            </>
          )}

          {searching && (
            <div className="space-y-1">
              <h2 className={SECTION_HEADING_CLASS}>{t('gardensPage.searching', { q: query })}</h2>
              {!plants.list.loading && plants.list.total > 0 && (
                <p className="text-base text-muted-foreground" aria-live="polite">{t(found, { count: plants.list.gardens, q: query })}</p>
              )}
            </div>
          )}

          {failed
            ? <ServerFailureNote error={failed} where="Gardens on Grow" className="text-base text-muted-foreground" />
            : null}

          {list.loading && list.page === 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('gardensPage.loading')}</div>
          )}

          {empty && (
            <p className="text-muted-foreground">{searching ? t('gardensPage.none', { q: query }) : t('gardensPage.empty')}</p>
          )}

          {!searching && gardens.list.items.length > 0 && (
            <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {gardens.list.items.map((garden) => <GardenRow key={garden.slug} garden={garden} />)}
            </ul>
          )}
          {searching && plants.list.items.length > 0 && (
            <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 xl:grid-cols-5">
              {plants.list.items.map((plant, index) => <PlantResult key={`${plant.garden.slug}-${plant.slug ?? plant.name}-${index}`} plant={plant} />)}
            </ul>
          )}

          <ShowMore list={list as Reading<unknown>} more={searching ? plants.more : gardens.more} />
        </div>
      </GardenFrame>
    </div>
  );
};

/** The gardens directory page, as the route's default import. */
export default GardensPage;
